import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './AdminProfileVisits.css';

export default function AdminProfileVisits() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'dancer', 'studio'
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsAdmin(currentUser?.email === 'giannisaliev@gmail.com');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
    }
  }, [isAdmin, filter]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const functions = getFunctions();
      const getProfileVisitStats = httpsCallable(functions, 'getProfileVisitStats');
      
      const params = filter !== 'all' ? { profileType: filter, limit: 100 } : { limit: 100 };
      const result = await getProfileVisitStats(params);
      
      setStats(result.data.stats || []);
    } catch (err) {
      console.error('Error loading profile visit stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="admin-profile-visits">
        <h2>Please log in to view this page</h2>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-profile-visits">
        <h2>Unauthorized Access</h2>
        <p>You must be an admin to view profile visit statistics.</p>
      </div>
    );
  }

  return (
    <div className="admin-profile-visits">
      <div className="header-section">
        <h1>📊 Profile Visit Statistics</h1>
        <p className="subtitle">Track visits to dancer and studio profiles</p>
      </div>

      <div className="filter-section">
        <label>Filter by type:</label>
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Profiles</option>
          <option value="dancer">Dancers Only</option>
          <option value="studio">Studios Only</option>
        </select>
        <button onClick={loadStats} className="refresh-btn" disabled={loading}>
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading statistics...</p>
        </div>
      ) : (
        <div className="stats-container">
          <div className="stats-summary">
            <div className="summary-card">
              <h3>{stats.length}</h3>
              <p>Total Profiles</p>
            </div>
            <div className="summary-card">
              <h3>{stats.reduce((sum, s) => sum + s.totalVisits, 0).toLocaleString()}</h3>
              <p>Total Visits</p>
            </div>
            <div className="summary-card">
              <h3>{stats.filter(s => s.profileType === 'dancer').length}</h3>
              <p>Dancer Profiles</p>
            </div>
            <div className="summary-card">
              <h3>{stats.filter(s => s.profileType === 'studio').length}</h3>
              <p>Studio Profiles</p>
            </div>
          </div>

          {stats.length > 0 ? (
            <table className="stats-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Profile Name</th>
                  <th>Total Visits</th>
                  <th>Active IPs (Last Hour)</th>
                  <th>First Visit</th>
                  <th>Last Visit</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((stat, index) => (
                  <tr key={stat.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className={`type-badge ${stat.profileType}`}>
                        {stat.profileType === 'dancer' ? '💃' : '🏢'} {stat.profileType}
                      </span>
                    </td>
                    <td>
                      <Link 
                        to={stat.profileType === 'dancer' 
                          ? `/dancer/${encodeURIComponent(stat.profileName)}`
                          : `/studio/${encodeURIComponent(stat.profileName)}`
                        }
                        className="profile-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {stat.profileName}
                      </Link>
                    </td>
                    <td>
                      <strong>{stat.totalVisits.toLocaleString()}</strong>
                    </td>
                    <td>{stat.uniqueRecentIPs}</td>
                    <td className="date-cell">{formatDate(stat.firstVisit)}</td>
                    <td className="date-cell">{formatDate(stat.lastVisit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              <p>No visit data available yet. Visits will be tracked as users view profiles.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
